import { useContext } from "react";
import isMatch from "lodash/isMatch";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Tag } from "@blueprintjs/core";
import {
  faCabinetFiling,
  faGlobeAmericas,
  faLock,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AddToPolicyButton } from "@components";

import { HeaderActionButton } from "@pages/editor/components/HeaderActionButton";
import { SignInButton } from "@pages/editor/components/SignInButton";
import { WorkbenchContext } from "@pages/editor/providers";
import { RegistryAddress } from "@pages/editor/stores";
import { fetchPolicy, savePolicy } from "@pages/policy/api";
import { PolicyRule } from "@pages/policy/types";

import EditorEllipsisMenu from "./EditorEllipsisMenu";
import { EditorTitle } from "./EditorTitle";
import { LastEdit } from "./LastEdit";

export const PurpleHeaderContainer = styled.div`
  padding: 12px;
  padding-right: 24px;
  background-color: var(--global-editor-header-purple);
  display: flex;
  align-items: center;
  gap: 8px;
  /* Nothing in header should wrap */
  white-space: nowrap;
  overflow: hidden;
`;

const Header = () => {
  const { workbench } = useContext(WorkbenchContext);
  const onAddToPolicy = async (slug: string) => {
    const { policy } = await fetchPolicy(workbench.org!.id, slug);
    if (workbench.hasUnsavedChanges) {
      // Add to Policy button will handle any errors
      await workbench.saveBundle();
    }

    if (!workbench.address) {
      // this error should never be thrown
      throw new Error("Rule has undefined address");
    }

    let newRule: PolicyRule;
    if (workbench.address instanceof RegistryAddress) {
      newRule = { type: "rule", id: workbench.address.fullName };
    } else {
      newRule = { type: "snippet", id: workbench.address.fullName };
    }

    // bail if it's already in the policy
    if (
      policy.rules.some((existingRule) =>
        isMatch({ ...existingRule, id: existingRule.id.toLowerCase() }, newRule)
      )
    )
      return [];

    policy.rules.push(newRule);
    await savePolicy(workbench.org!.id, policy);
    return [newRule];
  };

  const visibility = workbench.bundle?.visibility;

  return (
    <PurpleHeaderContainer className="bp3-dark">
      <EditorTitle />
      <LastEdit />
      <div style={{ marginRight: "auto" }} />
      {workbench.hasUnsavedChanges && (
        <Tag round intent="warning">
          Unsaved changes
        </Tag>
      )}
      <HeaderActionButton />
      <Button
        disabled={workbench.isLoading}
        outlined
        onClick={async () => {
          if (
            workbench.hasUnsavedChanges &&
            workbench.org &&
            workbench.bundle &&
            workbench.doesUserHaveEditPermission
          ) {
            await workbench.saveBundle();
          }
          workbench.ui.openRuleShareDialog();
        }}
        style={{ flexShrink: 0 }}
      >
        <FontAwesomeIcon
          icon={
            visibility === "unlisted"
              ? faGlobeAmericas
              : visibility === "org_private"
              ? faLock
              : faCabinetFiling
          }
        />{" "}
        Share
      </Button>
      {!workbench.user ? (
        <SignInButton />
      ) : (
        <AddToPolicyButton
          toAdd={
            !workbench.hasUnsavedChanges && workbench.address
              ? [workbench.address?.fullName]
              : ["custom rule"]
          }
          onSelect={onAddToPolicy}
          showOpenInAppCTA={workbench.isPlayground}
        />
      )}
      <EditorEllipsisMenu disabled={workbench.isLoading}></EditorEllipsisMenu>
    </PurpleHeaderContainer>
  );
};

export const EditorHeader = observer(Header);
